import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/lib/currencyUtils";
import { ChefHat, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

const CraftMarginSlider = ({ totalCost, menuPrice, currency }) => {
  const { t, i18n } = useTranslation();
  
  // Calculate percentages
  const cost = parseFloat(totalCost) || 0;
  const price = parseFloat(menuPrice) || 0;
  
  const foodCostPercent = price > 0 ? (cost / price) * 100 : 0;
  const grossProfitPercent = price > 0 ? ((price - cost) / price) * 100 : 0;
  
  const grossProfit = price - cost;
  
  // Determine color based on balance
  const getCraftColor = () => {
    if (foodCostPercent > 40) return "bg-red-500";
    if (foodCostPercent > 30) return "bg-amber-500";
    return "bg-amber-600";
  };
  
  const getMarginColor = () => {
    if (grossProfitPercent < 60) return "bg-red-500";
    if (grossProfitPercent < 70) return "bg-amber-500";
    return "bg-green-500";
  };

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl border border-slate-700 p-6 shadow-xl" data-testid="craft-margin-slider">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-white font-manrope mb-2">
          {t("craftMargin.title")}
        </h3>
        <p className="text-sm text-slate-400">{t("craftMargin.subtitle")}</p>
      </div>

      {/* The Slider Bar */}
      <div className="mb-8">
        {/* Labels */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ChefHat className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-semibold text-amber-500">
              {t("craftMargin.theCraft")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-green-500">
              {t("craftMargin.theMargin")}
            </span>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
        </div>

        {/* Visual Slider */}
        <div className="relative h-16 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
          {/* Craft Section (Left) */}
          <div
            className={cn(
              "absolute left-0 top-0 bottom-0 transition-all duration-500 ease-out",
              getCraftColor()
            )}
            style={{ width: `${Math.min(foodCostPercent, 100)}%` }}
            data-testid="craft-section"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {foodCostPercent > 15 && (
                <span className="text-white font-bold text-lg font-mono">
                  {foodCostPercent.toFixed(1)}%
                </span>
              )}
            </div>
          </div>

          {/* Margin Section (Right) */}
          <div
            className={cn(
              "absolute right-0 top-0 bottom-0 transition-all duration-500 ease-out",
              getMarginColor()
            )}
            style={{ width: `${Math.min(grossProfitPercent, 100)}%` }}
            data-testid="margin-section"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {grossProfitPercent > 15 && (
                <span className="text-white font-bold text-lg font-mono">
                  {grossProfitPercent.toFixed(1)}%
                </span>
              )}
            </div>
          </div>

          {/* Center Divider */}
          {foodCostPercent > 0 && foodCostPercent < 100 && (
            <div
              className="absolute top-0 bottom-0 w-1 bg-slate-950 z-10"
              style={{ left: `${foodCostPercent}%` }}
            />
          )}
        </div>

        {/* Balance Tip */}
        <div className="mt-3 text-center">
          <p className="text-xs text-slate-500 italic">
            {t("craftMargin.balanceTip")}
          </p>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-2 gap-4">
        {/* The Craft Details */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-3">
            <ChefHat className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold text-amber-500">
              {t("craftMargin.theCraft")}
            </span>
          </div>
          
          <div className="space-y-2">
            <div>
              <div className="text-xs text-slate-400 mb-1">
                {t("craftMargin.foodCostPercent")}
              </div>
              <div className="text-2xl font-bold text-amber-500 font-mono">
                {foodCostPercent.toFixed(1)}%
              </div>
            </div>
            
            <div>
              <div className="text-xs text-slate-400 mb-1">
                {t("craftMargin.foodCost")}
              </div>
              <div className="text-lg font-semibold text-white font-mono">
                {formatCurrency(totalCost, currency, 2, i18n.language)}
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-700">
            <p className="text-xs text-slate-400">
              {t("craftMargin.craftDesc")}
            </p>
          </div>
        </div>

        {/* The Margin Details */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-green-500/20">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-sm font-semibold text-green-500">
              {t("craftMargin.theMargin")}
            </span>
          </div>
          
          <div className="space-y-2">
            <div>
              <div className="text-xs text-slate-400 mb-1">
                {t("craftMargin.grossProfitPercent")}
              </div>
              <div className="text-2xl font-bold text-green-500 font-mono">
                {grossProfitPercent.toFixed(1)}%
              </div>
            </div>
            
            <div>
              <div className="text-xs text-slate-400 mb-1">
                {t("craftMargin.grossProfit")}
              </div>
              <div className="text-lg font-semibold text-white font-mono">
                {formatCurrency(grossProfit.toFixed(2), currency, 2, i18n.language)}
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-700">
            <p className="text-xs text-slate-400">
              {t("craftMargin.marginDesc")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CraftMarginSlider;
